import { getSynthsForNetwork } from '@kwenta/sdk/data'
import { createSelector } from '@reduxjs/toolkit'

import { DEFAULT_NETWORK_ID } from 'constants/defaults'
import type { RootState } from 'state/store'

const SUPPORTED_NETWORKS = [1, 10, 5, 420]

export const selectSignerWallet = (state: RootState) => state.wallet.walletAddress.signerWallet

export const selectWatcherWallet = (state: RootState) => state.wallet.walletAddress.watcherWallet

export const selectSubaccountWallet = (state: RootState) =>
	state.wallet.walletAddress.subAccountWallet

export const selectIsSignerMode = (state: RootState) =>
	state.wallet.walletAddress.selectedType === 'signer'

export const selectIsWatcherMode = (state: RootState) =>
	state.wallet.walletAddress.selectedType === 'watcher'

export const selectIsSubAccountMode = (state: RootState) =>
	state.wallet.walletAddress.selectedType === 'subAccount'

export const selectWallet = createSelector(
	selectIsSignerMode,
	selectIsSubAccountMode,
	selectSignerWallet,
	selectWatcherWallet,
	selectSubaccountWallet,
	(isSignerMode, isSubAccountMode, signerWallet, watcherWallet, subAccountWallet) => {
		if (isSignerMode) return signerWallet
		if (isSubAccountMode) return subAccountWallet
		return watcherWallet
	}
)

export const selectProviderNetwork = (state: RootState) =>
	state.wallet.providerNetworkId ?? DEFAULT_NETWORK_ID

export const selectAbstractionAddress = (state: RootState) => state.wallet.abstractionAddress

export const selectSignerNetwork = (state: RootState) => state.wallet.signerNetworkId

export const selectIsUnsupportedSignerNetwork = createSelector(
	(state: RootState) => state.wallet.providerNetworkId,
	(networkId) => networkId && !SUPPORTED_NETWORKS.includes(networkId)
)

export const selectIsWalletConnected = createSelector(
	(state: RootState) => state.wallet.walletAddress,
	(walletAddress) => !!walletAddress
)

export const selectIsL2 = createSelector(
	(state: RootState) => state.wallet.providerNetworkId,
	(networkId) => networkId && (networkId === 10 || networkId === 420)
)

export const selectIsL1 = createSelector(
	(state: RootState) => state.wallet.providerNetworkId,
	(networkId) => networkId && (networkId === 1 || networkId === 5)
)

export const selectIsTestnet = createSelector(
	(state: RootState) => state.wallet.providerNetworkId,
	(networkId) => networkId && (networkId === 5 || networkId === 420)
)

export const selectIsMainnet = createSelector(
	(state: RootState) => state.wallet.providerNetworkId,
	(networkId) => networkId && (networkId === 5 || networkId === 420)
)

export const selectSynthsMap = createSelector(
	(state: RootState) => state.wallet.providerNetworkId,
	(networkId) => (networkId ? getSynthsForNetwork(networkId) : {})
)
